import { useSyncExternalStore } from "react";

/**
 * The width from which the application draws its frame and the rail inside it.
 *
 * One number for both, because they are one decision. A frame drawn around a
 * rail that is not drawn is a band of empty ground where a rail is about to be,
 * and the entry screen used to ask this question for itself at 1080 while the
 * rail answered it at 1180 — so a hundred pixels of window got the frame
 * without its rail.
 *
 * Tailwind needs the number as a literal inside the class string, so `AppShell`
 * spells it `min-[1180px]:flex` and this spells it again. The two move
 * together: the rail appearing and the screen deciding it has a frame have to
 * happen at the same pixel. A room is not drawn in this frame and does not ask.
 */
export const RAIL_MIN_WIDTH = 1180;

const query = `(min-width: ${RAIL_MIN_WIDTH}px)`;

const subscribe = (onChange: () => void) => {
  const media = window.matchMedia(query);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
};

// Read from the window rather than from the query, which is what `useIsMobile`
// beside this does: the media query is how a change is heard, and the width is
// how the answer is given. They must not be two different readings.
const hasRail = () => window.innerWidth >= RAIL_MIN_WIDTH;

/**
 * Whether the frame and its rail are drawn.
 *
 * Read rather than inferred from CSS, because what the screen draws beside the
 * rail depends on it, and a rail hidden by `display: none` is still in the
 * document.
 *
 * Server snapshot: no rail. The entry screen is rendered on the server, and
 * guessing wide there would flash a rail onto a phone.
 */
export function useHasRail() {
  return useSyncExternalStore(subscribe, hasRail, () => false);
}
